import { supabase } from './supabaseClient';

/**
 * Получить текущего пользователя
 */
async function getCurrentUser() {
  const currentUserStr = localStorage.getItem('currentUser');
  if (currentUserStr) {
    try {
      const user = JSON.parse(currentUserStr);
      if (user && user.id) {
        // Пробуем teachers (для преподавателей)
        const { data: teacherData, error: teacherError } = await supabase
          .from('teachers')
          .select('*')
          .eq('auth_user_id', user.id)
          .maybeSingle();
        
        if (!teacherError && teacherData) {
          return {
            ...teacherData,
            id: teacherData.auth_user_id,
            role: 'teacher'
          };
        }
        
        // Пробуем students (для студентов)
        const { data: studentData, error: studentError } = await supabase
          .from('students')
          .select('*')
          .eq('user_account_id', user.id)
          .maybeSingle();
        
        if (!studentError && studentData) {
          return {
            ...studentData,
            id: user.id,
            role: 'student'
          };
        }
        
        // Если не нашли, возвращаем из localStorage
        return user;
      }
    } catch (e) {
      console.error('Error parsing currentUser from localStorage:', e);
    }
  }
  
  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (authUser) {
    const { data: teacherData, error: teacherError } = await supabase
      .from('teachers')
      .select('*')
      .eq('auth_user_id', authUser.id)
      .maybeSingle();
    
    if (!teacherError && teacherData) {
      return {
        ...teacherData,
        id: teacherData.auth_user_id,
        role: 'teacher'
      };
    }
    
    const { data: studentData, error: studentError } = await supabase
      .from('students')
      .select('*')
      .eq('user_account_id', authUser.id)
      .maybeSingle();
    
    if (!studentError && studentData) {
      return {
        ...studentData,
        id: authUser.id,
        role: 'student'
      };
    }
  }
  
  return null;
}

/**
 * Нормализовать studentId - всегда возвращать UUID
 */
async function normalizeStudentId(studentId) {
  if (!studentId) return null;
  
  // Если это уже UUID, возвращаем как есть
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(studentId);
  if (isUUID) {
    return studentId;
  }
  
  // Если это slug, находим UUID
  try {
    const { data: studentData } = await supabase
      .from('students')
      .select('id')
      .eq('invite_slug', studentId)
      .maybeSingle();
    
    return studentData?.id || studentId;
  } catch (error) {
    console.error('Error normalizing student ID:', error);
    return studentId;
  }
}

/**
 * Fallback: загрузить словарь из localStorage
 */
function getDictionaryFromLocalStorage(studentId) {
  try {
    const stored = localStorage.getItem(`dictionaryEntries_${studentId}`);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading dictionary from localStorage:', error);
    return [];
  }
}

/**
 * Получить словарь студента
 */
export async function getDictionary(studentId) {
  try {
    console.log('getDictionary called with studentId:', studentId);
    
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      return getDictionaryFromLocalStorage(studentId);
    }

    // Если studentId - это UUID (из таблицы students), используем его напрямую
    // Если это slug, нужно сначала найти student.id по slug
    let actualStudentId = studentId;
    
    // Проверяем, является ли studentId UUID или slug
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(studentId);
    
    if (!isUUID) {
      // Если это slug, находим student.id по invite_slug
      const { data: studentData } = await supabase
        .from('students')
        .select('id')
        .eq('invite_slug', studentId)
        .maybeSingle();
      
      if (studentData) {
        actualStudentId = studentData.id;
        console.log('Found student ID by slug:', actualStudentId);
      } else {
        console.warn('Student not found by slug:', studentId);
        // Fallback на localStorage
        return getDictionaryFromLocalStorage(studentId);
      }
    }

    console.log('getDictionary - querying Supabase with student_id:', actualStudentId);
    console.log('getDictionary - current user:', user);
    
    const { data, error } = await supabase
      .from('dictionary')
      .select('*')
      .eq('student_id', actualStudentId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching dictionary from Supabase:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      return getDictionaryFromLocalStorage(studentId);
    }

    console.log('getDictionary - raw data from Supabase:', data);
    console.log('getDictionary - loaded entries:', data?.length || 0);
    
    if (!data || data.length === 0) {
      console.warn('getDictionary - no entries found for student_id:', actualStudentId);
      // Проверяем, есть ли слова в localStorage как fallback
      const localEntries = getDictionaryFromLocalStorage(studentId);
      if (localEntries.length > 0) {
        console.log('getDictionary - found entries in localStorage:', localEntries.length);
        return localEntries;
      }
    }
    
    // Map entries - use level from database or localStorage
    const localEntries = getDictionaryFromLocalStorage(studentId);
    const localLevelMap = new Map();
    localEntries.forEach(e => {
      if (e.id && e.level) {
        localLevelMap.set(e.id, e.level);
      }
    });
    
    return data.map(entry => {
      // Use level from localStorage or database, or null if not available
      let level = localLevelMap.get(entry.id) || entry.level || null;
      
      return {
        id: entry.id,
        word: entry.word || '',
        translation: entry.translation || '',
        example: entry.example || '',
        level: level,
        type: entry.type || 'regular',
        partOfSpeech: entry.part_of_speech || null,
        createdAt: entry.created_at || new Date().toISOString()
      };
    });
  } catch (error) {
    console.error('Error in getDictionary:', error);
    return getDictionaryFromLocalStorage(studentId);
  }
}

/**
 * Добавить слово в словарь
 */
export async function addWord(studentId, wordData) {
  try {
    console.log('addWord called with studentId:', studentId);
    
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      const entries = getDictionaryFromLocalStorage(studentId);
      const newEntry = {
        id: Date.now().toString(),
        ...wordData,
        createdAt: new Date().toISOString()
      };
      entries.push(newEntry);
      localStorage.setItem(`dictionaryEntries_${studentId}`, JSON.stringify(entries));
      return newEntry;
    }

    // Конвертируем studentId из slug в UUID, если нужно
    let actualStudentId = studentId;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(studentId);
    
    if (!isUUID) {
      const { data: studentData } = await supabase
        .from('students')
        .select('id')
        .eq('invite_slug', studentId)
        .maybeSingle();
      
      if (studentData) {
        actualStudentId = studentData.id;
        console.log('Found student ID by slug for addWord:', actualStudentId);
      } else {
        console.warn('Student not found by slug for addWord:', studentId);
        // Fallback на localStorage
        const entries = getDictionaryFromLocalStorage(studentId);
        const newEntry = {
          id: Date.now().toString(),
          ...wordData,
          createdAt: new Date().toISOString()
        };
        entries.push(newEntry);
        localStorage.setItem(`dictionaryEntries_${studentId}`, JSON.stringify(entries));
        return newEntry;
      }
    }

    console.log('🔄 Inserting word to Supabase:', {
      student_id: actualStudentId,
      word: wordData.word,
      translation: wordData.translation,
      example: wordData.example,
      level: wordData.level,
      type: wordData.type,
      part_of_speech: wordData.partOfSpeech
    });

    const { data, error } = await supabase
      .from('dictionary')
      .insert({
        student_id: actualStudentId,
        word: wordData.word || '',
        translation: wordData.translation || null,
        example: wordData.example || null,
        level: wordData.level || null,
        type: wordData.type || null,
        part_of_speech: wordData.partOfSpeech || null
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Error adding word to Supabase:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        student_id: actualStudentId,
        wordData: wordData
      });
      
      // Не используем fallback на localStorage - выбрасываем ошибку, чтобы пользователь знал о проблеме
      throw new Error(`Failed to save word to Supabase: ${error.message || JSON.stringify(error)}`);
    }

    console.log('✅ Word successfully saved to Supabase:', data);

    // Также сохраняем в localStorage для обратной совместимости
    // Используем level из wordData, если он есть, иначе 'B1' по умолчанию
    const newEntry = {
      id: data.id,
      word: data.word,
      translation: data.translation || '',
      example: data.example || '',
      level: wordData.level || data.level || 'B1', // Use level from wordData, not hardcoded B1
      type: wordData.type || data.type || 'regular',
      partOfSpeech: wordData.partOfSpeech || data.part_of_speech || null,
      createdAt: data.created_at || new Date().toISOString()
    };
    const entries = getDictionaryFromLocalStorage(studentId);
    entries.push(newEntry);
    localStorage.setItem(`dictionaryEntries_${studentId}`, JSON.stringify(entries));

    // Отправляем событие с нормализованным UUID для синхронизации между компонентами
    const normalizedId = await normalizeStudentId(actualStudentId);
    window.dispatchEvent(new CustomEvent('dictionaryUpdated', { 
      detail: { studentId: normalizedId } 
    }));

    return newEntry;
  } catch (error) {
    console.error('Error in addWord:', error);
    // Fallback на localStorage
    const entries = getDictionaryFromLocalStorage(studentId);
    const newEntry = {
      id: Date.now().toString(),
      ...wordData,
      createdAt: new Date().toISOString()
    };
    entries.push(newEntry);
    localStorage.setItem(`dictionaryEntries_${studentId}`, JSON.stringify(entries));
    return newEntry;
  }
}

/**
 * Обновить слово в словаре
 */
export async function updateWord(wordId, updates) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      // Находим слово во всех словарях студентов
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dictionaryEntries_')) {
          const entries = JSON.parse(localStorage.getItem(key) || '[]');
          const index = entries.findIndex(e => e.id === wordId);
          if (index !== -1) {
            entries[index] = { ...entries[index], ...updates };
            localStorage.setItem(key, JSON.stringify(entries));
            return entries[index];
          }
        }
      }
      return null;
    }

    const { data, error } = await supabase
      .from('dictionary')
      .update({
        word: updates.word,
        translation: updates.translation || null,
        example: updates.example || null,
        level: updates.level || null,
        type: updates.type || null,
        part_of_speech: updates.partOfSpeech || null
      })
      .eq('id', wordId)
      .select('*, student_id')
      .single();

    if (error) {
      console.error('Error updating word in Supabase:', error);
      // Fallback на localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dictionaryEntries_')) {
          const entries = JSON.parse(localStorage.getItem(key) || '[]');
          const index = entries.findIndex(e => e.id === wordId);
          if (index !== -1) {
            entries[index] = { ...entries[index], ...updates };
            localStorage.setItem(key, JSON.stringify(entries));
            return entries[index];
          }
        }
      }
      return null;
    }

    // Также обновляем в localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('dictionaryEntries_')) {
        const entries = JSON.parse(localStorage.getItem(key) || '[]');
        const index = entries.findIndex(e => e.id === wordId);
        if (index !== -1) {
          entries[index] = {
            id: data.id,
            word: data.word,
            translation: data.translation || '',
            example: data.example || '',
            level: updates.level || entries[index].level || 'B1', // Preserve existing level or use updates.level
            createdAt: data.created_at || new Date().toISOString()
          };
          localStorage.setItem(key, JSON.stringify(entries));
          
          // Отправляем событие с нормализованным UUID
          const studentIdFromKey = key.replace('dictionaryEntries_', '');
          const normalizedId = await normalizeStudentId(studentIdFromKey);
          window.dispatchEvent(new CustomEvent('dictionaryUpdated', { 
            detail: { studentId: normalizedId } 
          }));
          
          return entries[index];
        }
      }
    }

    // Отправляем событие - получаем studentId из записи
    if (data.student_id) {
      const normalizedId = await normalizeStudentId(data.student_id);
      window.dispatchEvent(new CustomEvent('dictionaryUpdated', { 
        detail: { studentId: normalizedId } 
      }));
    }

    return {
      id: data.id,
      word: data.word,
      translation: data.translation || '',
      example: data.example || '',
      level: updates.level || 'B1', // Use level from updates, not hardcoded B1
      createdAt: data.created_at || new Date().toISOString()
    };
  } catch (error) {
    console.error('Error in updateWord:', error);
    return null;
  }
}

/**
 * Удалить слово из словаря
 */
export async function deleteWord(wordId) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      // Находим и удаляем слово во всех словарях студентов
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dictionaryEntries_')) {
          const entries = JSON.parse(localStorage.getItem(key) || '[]');
          const filtered = entries.filter(e => e.id !== wordId);
          if (filtered.length !== entries.length) {
            localStorage.setItem(key, JSON.stringify(filtered));
            return true;
          }
        }
      }
      return true;
    }

    // Сначала получаем student_id из записи перед удалением
    const { data: wordData } = await supabase
      .from('dictionary')
      .select('student_id')
      .eq('id', wordId)
      .maybeSingle();

    const { error } = await supabase
      .from('dictionary')
      .delete()
      .eq('id', wordId);

    if (error) {
      console.error('Error deleting word from Supabase:', error);
      // Fallback на localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dictionaryEntries_')) {
          const entries = JSON.parse(localStorage.getItem(key) || '[]');
          const filtered = entries.filter(e => e.id !== wordId);
          if (filtered.length !== entries.length) {
            localStorage.setItem(key, JSON.stringify(filtered));
            // Отправляем событие
            const studentIdFromKey = key.replace('dictionaryEntries_', '');
            const normalizedId = await normalizeStudentId(studentIdFromKey);
            window.dispatchEvent(new CustomEvent('dictionaryUpdated', { 
              detail: { studentId: normalizedId } 
            }));
            return true;
          }
        }
      }
      return true;
    }

    // Также удаляем из localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('dictionaryEntries_')) {
        const entries = JSON.parse(localStorage.getItem(key) || '[]');
        const filtered = entries.filter(e => e.id !== wordId);
        if (filtered.length !== entries.length) {
          localStorage.setItem(key, JSON.stringify(filtered));
        }
      }
    }

    // Отправляем событие с нормализованным UUID
    if (wordData?.student_id) {
      const normalizedId = await normalizeStudentId(wordData.student_id);
      window.dispatchEvent(new CustomEvent('dictionaryUpdated', { 
        detail: { studentId: normalizedId } 
      }));
    }

    return true;
  } catch (error) {
    console.error('Error in deleteWord:', error);
    return true;
  }
}

/**
 * Поиск по словарю
 */
export async function searchWords(studentId, query) {
  try {
    const dictionary = await getDictionary(studentId);
    if (!query) return dictionary;

    const lowerQuery = query.toLowerCase();
    return dictionary.filter(entry => 
      entry.word.toLowerCase().includes(lowerQuery) ||
      (entry.translation && entry.translation.toLowerCase().includes(lowerQuery)) ||
      (entry.example && entry.example.toLowerCase().includes(lowerQuery))
    );
  } catch (error) {
    console.error('Error in searchWords:', error);
    return [];
  }
}

